"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { Bot, FileText, Lightbulb, Search, Sparkles, Stethoscope, User, X } from "lucide-react";

import { getAISearchSuggestions, performAISearch, trackSearchResultClick } from "@/app/actions/search-actions";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface SearchResult {
  type: "patient" | "document" | "procedure";
  id: string;
  title: string;
  subtitle: string;
  metadata: Record<string, any>;
  relevanceScore?: number;
  matchReason?: string;
  highlight?: Record<string, any>;
}

interface SearchInterpretation {
  intent: string;
  entities: Record<string, any>;
  searchTerms: string[];
  confidence: number;
  explanation: string;
}

interface AIEnhancedSearchProps {
  onPatientSelect?: (patient: any) => void;
  onDocumentSelect?: (document: any) => void;
  placeholder?: string;
  defaultTab?: string;
}

export function AIEnhancedSearch({
  onPatientSelect,
  onDocumentSelect,
  placeholder = "Ask a question or search with natural language...",
  defaultTab = "all",
}: AIEnhancedSearchProps) {
  const [query, setQuery] = useState("");
  const [activeTab, _setActiveTab] = useState(defaultTab);
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [interpretation, setInterpretation] = useState<SearchInterpretation | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [aiSearchEnabled, setAiSearchEnabled] = useState(true);
  const [showInterpretation, setShowInterpretation] = useState(true);
  const [queryId, setQueryId] = useState<string | null>(null);
  const [_isCached, setIsCached] = useState(false);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const formatAIResults = useCallback((results: any[]): SearchResult[] => {
    return results.map((result) => {
      const type = result.resultType || "patient";

      if (type === "patient") {
        return {
          type: "patient" as const,
          id: result.id,
          title: `${result.firstName} ${result.lastName}`,
          subtitle: `${result.mrn} • ${result.specialty || "N/A"} • ${result.status}`,
          metadata: result,
          relevanceScore: result.relevanceScore,
          matchReason: result.matchReason,
        };
      }

      if (type === "document") {
        const docDate = result.documentDate || result.uploadDate;
        const formattedDate = docDate ? new Date(docDate).toLocaleDateString() : "No date";
        return {
          type: "document" as const,
          id: result.id,
          title: result.title,
          subtitle: `${result.category} • ${result.type} • ${formattedDate}`,
          metadata: result,
          relevanceScore: result.relevanceScore,
          matchReason: result.matchReason,
        };
      }

      if (type === "procedure") {
        const procDate = result.performedDate ? new Date(result.performedDate).toLocaleDateString() : "No date";
        return {
          type: "procedure" as const,
          id: result.id,
          title: result.name,
          subtitle: `${result.code || "No CPT"} • ${result.performedBy} • ${procDate}`,
          metadata: result,
          relevanceScore: result.relevanceScore,
          matchReason: result.matchReason,
        };
      }

      return {
        type: type as any,
        id: result.id,
        title: result.title || result.name,
        subtitle: result.subtitle || "",
        metadata: result,
        relevanceScore: result.relevanceScore,
        matchReason: result.matchReason,
      };
    });
  }, []);

  const performSearch = useCallback(async () => {
    if (!query.trim()) return;

    setIsLoading(true);

    try {
      if (aiSearchEnabled) {
        // Use AI-powered search via server action
        const result = await performAISearch({
          query,
          searchType: activeTab === "all" ? "patients" : (activeTab as "patients" | "documents" | "procedures"),
          includeInterpretation: showInterpretation,
          maxResults: 20,
        });

        if (result.success && result.data) {
          setResults(formatAIResults(result.data.results));
          if (result.data.interpretation) {
            setInterpretation(result.data.interpretation);
          }
          // Store queryId and cache status for analytics
          if (result.data.queryId) {
            setQueryId(result.data.queryId);
          }
          if (result.data.cached !== undefined) {
            setIsCached(result.data.cached);
          }
        } else {
          setResults([]);
        }
      } else {
        // Fallback to traditional keyword search
        // This would call the regular Algolia search
        setResults([]);
      }
    } catch (_error) {
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, [query, aiSearchEnabled, activeTab, showInterpretation, formatAIResults]);

  const fetchAISuggestions = useCallback(async () => {
    if (query.length < 3) return;

    try {
      const result = await getAISearchSuggestions(query);
      if (result.success && result.suggestions) {
        setSuggestions(result.suggestions);
        setShowSuggestions(true);
      }
    } catch (_error) {}
  }, [query]);

  // Debounced search
  useEffect(() => {
    const searchTimer = setTimeout(() => {
      if (query.length >= 2) {
        performSearch();
        if (aiSearchEnabled) {
          fetchAISuggestions();
        }
      } else {
        setResults([]);
        setSuggestions([]);
        setInterpretation(null);
        setShowSuggestions(false);
      }
    }, 400);

    return () => clearTimeout(searchTimer);
  }, [query, aiSearchEnabled, fetchAISuggestions, performSearch]);

  // Hide suggestions when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleResultClick = async (result: SearchResult) => {
    // Track click analytics if queryId is available
    if (queryId && aiSearchEnabled) {
      try {
        await trackSearchResultClick({
          queryId,
          resultId: result.id,
          resultType: result.type,
        });
      } catch (_error) {
        // Don't block user interaction on analytics failure
      }
    }

    if (result.type === "patient" && onPatientSelect) {
      onPatientSelect(result.metadata);
    } else if (result.type === "document" && onDocumentSelect) {
      onDocumentSelect(result.metadata);
    }
    setQuery("");
    setResults([]);
    setInterpretation(null);
    setShowSuggestions(false);
  };

  const handleSuggestionClick = (suggestion: string) => {
    setQuery(suggestion);
    setShowSuggestions(false);
    searchInputRef.current?.focus();
  };

  const clearSearch = () => {
    setQuery("");
    setResults([]);
    setInterpretation(null);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const getResultIcon = (type: string) => {
    switch (type) {
      case "patient":
        return <User className="h-4 w-4" />;
      case "document":
        return <FileText className="h-4 w-4" />;
      case "procedure":
        return <Stethoscope className="h-4 w-4" />;
      default:
        return <Search className="h-4 w-4" />;
    }
  };

  const getResultMeta = (type: string) => {
    switch (type) {
      case "patient":
        return { bg: "bg-blue-50", text: "text-blue-700", label: "Patient" };
      case "document":
        return { bg: "bg-emerald-50", text: "text-emerald-700", label: "Document" };
      case "procedure":
        return { bg: "bg-indigo-50", text: "text-indigo-700", label: "Procedure" };
      default:
        return { bg: "bg-slate-100", text: "text-slate-700", label: type };
    }
  };

  const exampleQueries = [
    "diabetic patients over 60",
    "recent chest x-rays",
    "colonoscopy procedures",
    "oncology patients in active treatment",
    "lab reports from last week",
    "cardiac procedures last month",
  ];

  return (
    <div className="relative w-full">
      {/* Search input — single, clean, prominent */}
      <div className="relative">
        <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input
          ref={searchInputRef}
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          className="h-12 rounded-lg border-slate-200 bg-white pl-11 pr-12 text-sm text-slate-900 placeholder:text-slate-400 focus-visible:border-blue-400 focus-visible:ring-2 focus-visible:ring-blue-100"
        />
        {query && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-1.5 top-1/2 h-8 w-8 -translate-y-1/2 text-slate-400 hover:text-slate-700"
            onClick={clearSearch}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Utility row — tiny AI status + interpretation toggle */}
      <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2 text-xs">
        <div className="flex items-center gap-3">
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => setAiSearchEnabled((v) => !v)}
                  className={`inline-flex items-center gap-1.5 rounded-md px-2 py-1 font-medium transition-colors ${
                    aiSearchEnabled
                      ? "bg-blue-50 text-blue-700 hover:bg-blue-100"
                      : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                  }`}
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${aiSearchEnabled ? "bg-blue-600" : "bg-slate-400"}`} />
                  AI search {aiSearchEnabled ? "on" : "off"}
                </button>
              </TooltipTrigger>
              <TooltipContent>Natural language and semantic understanding</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {aiSearchEnabled && (
            <button
              type="button"
              onClick={() => setShowInterpretation((v) => !v)}
              className="font-medium text-slate-500 underline-offset-4 transition-colors hover:text-slate-900 hover:underline"
            >
              {showInterpretation ? "Hide" : "Show"} AI reasoning
            </button>
          )}
        </div>
      </div>

      {/* Example queries — flat chips, no purple sparkle */}
      {aiSearchEnabled && !query && (
        <div className="mt-4">
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Try asking</p>
          <div className="flex flex-wrap gap-1.5">
            {exampleQueries.map((ex) => (
              <button
                key={ex}
                onClick={() => setQuery(ex)}
                className="inline-flex items-center rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600 transition-colors hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900"
              >
                {ex}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* AI-Powered Suggestions */}
      {showSuggestions && suggestions.length > 0 && aiSearchEnabled && (
        <div
          ref={suggestionsRef}
          className="absolute left-0 right-0 top-[3.25rem] z-50 max-h-64 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg"
        >
          <div className="border-b border-slate-200 bg-slate-50 px-3 py-2">
            <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              <Sparkles className="h-3 w-3 text-blue-600" />
              <span>Suggestions</span>
            </div>
          </div>
          {suggestions.map((suggestion, index) => (
            <button
              type="button"
              key={index}
              className="flex w-full items-center gap-3 border-b border-slate-100 px-4 py-2.5 text-left text-sm text-slate-700 transition-colors last:border-b-0 hover:bg-slate-50"
              onClick={() => handleSuggestionClick(suggestion)}
            >
              <Search className="h-3.5 w-3.5 shrink-0 text-slate-400" />
              <span>{suggestion}</span>
            </button>
          ))}
        </div>
      )}

      {/* Query Interpretation */}
      {interpretation && showInterpretation && aiSearchEnabled && (
        <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50/60 p-4">
          <div className="mb-2 flex items-center gap-2">
            <Lightbulb className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-semibold text-slate-900">AI reasoning</span>
            <span className="rounded-md bg-white px-1.5 py-0.5 text-[10px] font-medium text-slate-600 ring-1 ring-slate-200">
              {(interpretation.confidence * 100).toFixed(0)}% confidence
            </span>
          </div>
          <p className="text-sm text-slate-700">{interpretation.explanation}</p>
          {interpretation.searchTerms.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {interpretation.searchTerms.map((term, idx) => (
                <span
                  key={idx}
                  className="rounded-md bg-white px-2 py-0.5 text-xs font-medium text-slate-600 ring-1 ring-slate-200"
                >
                  {term}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Search Results */}
      {(isLoading || results.length > 0) && (
        <div className="mt-4 rounded-lg border border-slate-200 bg-white">
          <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
            <span className="text-sm font-semibold text-slate-900">Results</span>
            {results.length > 0 && (
              <span className="text-xs font-medium text-slate-500">
                {results.length} {results.length === 1 ? "result" : "results"}
              </span>
            )}
          </div>
          <div className="p-2">
            {isLoading ? (
              <div className="space-y-2 p-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse">
                    <div className="mb-2 h-4 w-3/4 rounded bg-slate-100"></div>
                    <div className="h-3 w-1/2 rounded bg-slate-100"></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-1">
                {results.map((result, index) => {
                  const meta = getResultMeta(result.type);
                  return (
                    <button
                      type="button"
                      key={`${result.type}-${result.id}-${index}`}
                      className="flex w-full items-start gap-3 rounded-md px-2.5 py-2.5 text-left transition-colors hover:bg-slate-50"
                      onClick={() => handleResultClick(result)}
                    >
                      <span
                        className={`mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${meta.bg} ${meta.text}`}
                      >
                        {getResultIcon(result.type)}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <div className="truncate text-sm font-medium text-slate-900">{result.title}</div>
                          {result.relevanceScore && aiSearchEnabled && (
                            <TooltipProvider delayDuration={200}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="shrink-0 rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-slate-600">
                                    {(result.relevanceScore * 100).toFixed(0)}%
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>AI relevance score</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                        <div className="mt-0.5 truncate text-xs text-slate-500">
                          <span className="font-medium text-slate-600">{meta.label}</span>
                          <span className="mx-1.5 text-slate-300">·</span>
                          {result.subtitle}
                        </div>
                        {result.matchReason && aiSearchEnabled && (
                          <div className="mt-1 flex items-center gap-1 text-[11px] text-blue-700">
                            <Sparkles className="h-3 w-3" />
                            <span className="truncate">{result.matchReason}</span>
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* No Results State */}
      {!isLoading && query.length >= 2 && results.length === 0 && (
        <div className="mt-4 rounded-lg border border-slate-200 bg-white px-6 py-8 text-center">
          <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-slate-100">
            <Search className="h-5 w-5 text-slate-400" />
          </div>
          <div className="text-sm font-medium text-slate-900">No results found</div>
          <div className="mt-1 text-xs text-slate-500">
            {aiSearchEnabled
              ? "Try rephrasing your query or using different medical terms"
              : "Try enabling AI search for better results"}
          </div>
          {!aiSearchEnabled && (
            <Button variant="outline" size="sm" onClick={() => setAiSearchEnabled(true)} className="mt-4">
              <Bot className="mr-2 h-4 w-4" />
              Enable AI search
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
